import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { serverUrl } from "../../ApiRoute";

// The auth service returns three cookies. access_token and refresh_token are
// HttpOnly, so JS never reads them and never needs to - the browser attaches
// them. csrf_token is deliberately readable so we can echo it back in a header
// (the double submit cookie pattern); the service rejects the refresh call with
// 403 when the header is missing or does not match the cookie.
const CSRF_COOKIE = "csrf_token";
const CSRF_HEADER = "x-csrf-token";
const DEVICE_ID_KEY = "jfl-device-id";

export const readCookie = (name) => {
  const escaped = name.replace(/([.*+?^=!:${}()|[\]/\\])/g, "\\$1");
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

// The service binds the authorization code and the refresh token to a deviceId,
// so it has to stay stable for this browser across reloads and refreshes.
export const getDeviceId = () => {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = uuidv4();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
};

export const jflApi = axios.create({
  baseURL: serverUrl.jflAuth,
  // Required: every token lives in a cookie, nothing is carried in the body.
  withCredentials: true,
});

// Read the csrf cookie per request rather than once at module load - a refresh
// rotates it, and a stale value gets a 403.
const attachCsrf = (config) => {
  const token = readCookie(CSRF_COOKIE);
  if (token) {
    config.headers[CSRF_HEADER] = token;
  }
  return config;
};

// Paths whose 401 means "these credentials are wrong", not "your session
// expired". Matched on the request path so a baseURL makes no difference.
const isAuthEndpoint = (url = "") => /\/v1\/auth\//.test(url);

let refreshPromise = null;

// Collapse concurrent refreshes into one call. Several requests failing with 401
// at the same time would otherwise each rotate the cookie, and all but the last
// would then be retrying with a token the service has already replaced.
const refreshSession = () => {
  if (!refreshPromise) {
    refreshPromise = jflApi
      .post(
        "/v1/auth/token",
        { grantType: "refresh_token" },
        { skipAuthRefresh: true }
      )
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

let onSessionExpired = () => {};

// Lets the app decide what "logged out" means without this module importing the
// store (which would be a require cycle through the redux actions).
export const setSessionExpiredHandler = (handler) => {
  onSessionExpired = handler;
};

// Exported so any other axios instance that authenticates with these cookies
// can get the same 401 -> refresh -> retry behaviour.
export const attachAuthRefresh = (instance) => {
  instance.interceptors.request.use(attachCsrf);

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const request = error.config;

      // skipAuthRefresh guards the refresh call itself: it answers 401 when the
      // refresh cookie is gone, and retrying it would recurse.
      //
      // The /v1/auth/* endpoints are excluded for a different reason: they
      // answer 401 about the CREDENTIALS they were given, not about this
      // session. Signing in with a wrong password, or changing a password with
      // the wrong current one, is a 401 that a refresh cannot fix - and
      // retrying it would silently send the request a second time.
      if (
        error.response?.status !== 401 ||
        !request ||
        request.retriedAfterRefresh ||
        request.skipAuthRefresh ||
        isAuthEndpoint(request.url)
      ) {
        return Promise.reject(error);
      }

      request.retriedAfterRefresh = true;

      try {
        await refreshSession();
      } catch (refreshError) {
        onSessionExpired();
        return Promise.reject(error);
      }

      return instance(request);
    }
  );

  return instance;
};

attachAuthRefresh(jflApi);
